import { useEffect, useMemo, useState } from 'react'
import { Icon } from '@iconify/react'
import { Input } from 'common/components/Input/Input'
import { Button } from 'common/components/Button/Button'
import { BUTTON_MODIFIER } from 'common/constants/buttonConstants'
import { useNotification } from 'app/providers'

import styles from './product-scanner.module.scss'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import { ModelDebugMode } from 'features/Procedure'
import { Modal } from 'antd'
import { formatScannedProductLabelPreview } from './product-scanner.utils'
import { findPreferredBarcode, type DetectedBarcode, ZxingBarcodeScanner } from '../barcode-scanner'

export type ScannerModalSubmitValue = {
  rawValue: string
  format: string
}

type ScannerModalProps = {
  isOpen: boolean
  onClose: () => void
  onSubmit: (scan: ScannerModalSubmitValue) => void
}

type CameraFacingMode = 'user' | 'environment'

const PRODUCT_LABEL_PREFERRED_FORMATS = [
  'data_matrix',
  'qr_code',
  'upc_a',
  'upc_e',
  'ean_13',
  'ean_8',
]
const DEFAULT_FACING_MODE: CameraFacingMode = 'user'

export const ScannerModal = ({ isOpen, onClose, onSubmit }: ScannerModalProps) => {
  const [scannedValue, setScannedValue] = useState<ScannerModalSubmitValue | null>(null)
  const [facingMode, setFacingMode] = useState<CameraFacingMode>(DEFAULT_FACING_MODE)
  const { setNotification } = useNotification()

  const scannerConstraints = useMemo<MediaTrackConstraints>(
    () => ({
      facingMode: { exact: facingMode },
      width: { ideal: 1920 },
      height: { ideal: 1080 },
    }),
    [facingMode],
  )

  useEffect(() => {
    if (!isOpen) {
      setScannedValue(null)
      setFacingMode(DEFAULT_FACING_MODE)
    }
  }, [isOpen])

  const handleFlipCamera = () => {
    setFacingMode((current) => (current === 'user' ? 'environment' : 'user'))
  }

  const handleScanSuccess = (result: DetectedBarcode[]) => {
    if (ModelDebugMode) console.log('result', result)
    const preferredResult = findPreferredBarcode(result, PRODUCT_LABEL_PREFERRED_FORMATS)

    if (preferredResult?.rawValue) {
      setScannedValue({
        rawValue: preferredResult.rawValue,
        format: preferredResult.format,
      })
    }
  }

  const handleScanError = (error: any) => {
    setNotification({
      type: ALERT_CONSTANTS.ERROR,
      duration: 2,
      title: 'Scan error',
      description: error?.message ?? 'Something went wrong',
    })
  }

  const scannedValuePreview = formatScannedProductLabelPreview(
    scannedValue?.rawValue || '',
    scannedValue?.format,
  )

  return (
    <Modal
      centered
      destroyOnHidden
      title='Scan Product'
      open={isOpen}
      onCancel={onClose}
      footer={null}
      classNames={{ content: styles.modalContainer }}>
      <div className={styles.scannerContainer}>
        <div className={styles.scannerViewport}>
          <ZxingBarcodeScanner
            onScan={handleScanSuccess}
            onError={handleScanError}
            constraints={scannerConstraints}
            scanDelay={200}
            mirror={facingMode === 'user'}
          />
          <button
            type='button'
            className={styles.flipCameraButton}
            onClick={handleFlipCamera}
            aria-label={facingMode === 'user' ? 'Switch to back camera' : 'Switch to front camera'}
            title={facingMode === 'user' ? 'Switch to back camera' : 'Switch to front camera'}>
            <Icon icon='mdi:camera-flip-outline' width={22} height={22} />
          </button>
        </div>

        <div className={styles.scannerInput}>
          <Input
            name='label'
            label='Scanned value'
            value={scannedValuePreview}
            readOnly
            placeholder='Scanned value will appear here'
          />
        </div>

        <div className={styles.scannerActions}>
          <Button modifier={BUTTON_MODIFIER.SECONDARY} onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => scannedValue && onSubmit(scannedValue)}
            disabled={!scannedValue?.rawValue.trim()}>
            Add Product
          </Button>
        </div>
      </div>
    </Modal>
  )
}
